import type { FC, MouseEventHandler } from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { SubmitHandler } from "react-hook-form";
import { useLocation } from "react-router-dom";
import { type Breadcrumb, useBreadcrumb } from "../../../components/Breadcrumb";
import { ButtonUI } from "../../../components/Button";
import { LoadingUI } from "../../../components/Loading";
import { PageTitle } from "../../../components/PageTitle";
import { ToggleUI } from "../../../components/Toggle";
import { SEARCH_LIMIT } from "../../../constants/search-fiilter";
import type { EmailSearchInput } from "../../../hooks/useEmailsRestApi";
import type {
  EmailsWithStatus,
  EmailWithStatus,
  SelectContextMenuHandler,
} from ".";
import { EmailsCardsUI } from "./components/EmailsCardsUI";
import { EmailsTableUI } from "./components/EmailsTableUI";
import { SearchEmailStats } from "./components/SearchEmailStats";
import {
  type ChangeIsMatterHandler,
  SearchEmailsFormUI,
} from "./components/SearchEmailsForm";

type SearchEmailsPageProps = {
  isLoading: boolean;
  isMatter: boolean;
  currentSearchInput: EmailSearchInput;
  invisibleEmailNums: number;
  matterEmails: EmailsWithStatus;
  hrEmails: EmailsWithStatus;
  selectedEmail: EmailWithStatus;
  onSelectEmail: (id: string) => void;
  onToggleFavoriteEmail: (emailId: string, favorite: boolean) => void;
  onSelectContextMenu: SelectContextMenuHandler;
  onSearch: SubmitHandler<EmailSearchInput>;
  onClearFilter: () => void;
  onChangeIsMatter: ChangeIsMatterHandler;
  onShowMore: MouseEventHandler;
};

const BREAD_CRUMBS: Breadcrumb[] = [
  {
    label: "ホーム",
    to: "/",
  },
  {
    label: "メール",
  },
  {
    label: "検索",
  },
];

// 選択したメールのID、親要素のスクロール位置を保持する変数
let scrollParentY: number | null = null;

const SearchEmailsPage: FC<SearchEmailsPageProps> = ({
  isLoading,
  isMatter,
  currentSearchInput,
  matterEmails,
  hrEmails,
  invisibleEmailNums,
  onToggleFavoriteEmail,
  onSelectEmail,
  onSelectContextMenu,
  onSearch,
  onClearFilter,
  onChangeIsMatter,
  onShowMore,
}) => {
  const { setBreadcrumbItems } = useBreadcrumb();

  // 表示形式の状態管理を追加
  const [viewMode, setViewMode] = useState<"card" | "table">("card");
  const viewModeLabel = viewMode === "card" ? "カード表示" : "テーブル表示";
  const activeEmails = useMemo(
    () => (isMatter ? matterEmails : hrEmails),
    [isMatter, matterEmails, hrEmails],
  );
  const favoriteCount = useMemo(
    () =>
      activeEmails.nodes.filter((node: EmailWithStatus) => node.favorite)
        .length,
    [activeEmails.nodes],
  );

  useEffect(() => {
    setBreadcrumbItems(BREAD_CRUMBS);
  }, [setBreadcrumbItems]);

  const rootDivRef = useRef<HTMLDivElement>(null);
  const scrollCardRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const initialSelectedEmailId = queryParams.get("emailId");

  // 選択したメールのID、親要素のスクロール位置を保持する変数
  const [selectedEmailId, setSelectedEmailId] = useState<string | null>(
    initialSelectedEmailId || null,
  );

  // スクロール対象の親要素（layoutコンポーネント）を取得する関数
  const getScrollParentElement = useCallback((): HTMLElement | null => {
    if (!rootDivRef.current) return null;
    let el: HTMLElement | null = rootDivRef.current.parentElement;
    while (el) {
      if (el.id === "layout-content-scroll") {
        return el;
      }
      el = el.parentElement;
    }
    return null;
  }, []);

  const handleSelectEmail = useCallback(
    (emailId: string): void => {
      // 親要素のスクロール位置を保存
      const parentElement = getScrollParentElement();
      scrollParentY = parentElement?.scrollTop ? parentElement.scrollTop : 0;

      // 既存のonSelectEmail呼び出し
      onSelectEmail(emailId);
    },
    [getScrollParentElement, onSelectEmail],
  );

  // スクロール位置の復元
  useEffect(() => {
    if (
      (isMatter && matterEmails.nodes.length > 0) ||
      (!isMatter && hrEmails.nodes.length > 0)
    ) {
      const parentElement = getScrollParentElement();
      if (
        scrollCardRef.current &&
        parentElement &&
        typeof scrollParentY === "number"
      ) {
        scrollCardRef.current.scrollIntoView();
        parentElement.scrollTop = scrollParentY;

        scrollParentY = null;
        setSelectedEmailId(null);

        const url = new URL(window.location.href);
        url.searchParams.delete("emailId");
        window.history.replaceState({}, document.title, url.toString());
      }
    }
  }, [
    getScrollParentElement,
    isMatter,
    matterEmails.nodes.length,
    hrEmails.nodes.length,
  ]);

  return (
    <div ref={rootDivRef}>
      <PageTitle>メール検索</PageTitle>
      <div className="flex justify-center mb-6">
        <div className="w-full max-w-6xl">
          <SearchEmailsFormUI
            isLoading={isLoading}
            currentSearchInput={currentSearchInput}
            isMatter={isMatter}
            onSearch={onSearch}
            onClearFilter={onClearFilter}
            onChangeIsMatter={onChangeIsMatter}
          />
        </div>
      </div>

      {isLoading ? (
        <div className="w-full flex justify-center pt-20">
          <LoadingUI type="Grid" />
        </div>
      ) : (
        <div className="flex flex-col">
          {activeEmails.nodes.length > 0 && (
            <div className="flex justify-center">
              <div className="w-full max-w-6xl flex items-center justify-between">
                <div className="text-base-content">
                  <SearchEmailStats
                    totalCount={activeEmails.totalCount}
                    displayCount={activeEmails.nodes.length}
                    invisibleCount={invisibleEmailNums}
                    favoriteCount={favoriteCount}
                  />
                </div>
                <ToggleUI
                  label={viewModeLabel}
                  toggleSize="toggle-sm"
                  checked={viewMode === "table"}
                  onChange={event =>
                    setViewMode(event.target.checked ? "table" : "card")
                  }
                />
              </div>
            </div>
          )}
          <div className="flex justify-center">
            <div className="w-full max-w-6xl">
              <div className="text-base-content">
                <div
                  className={
                    viewMode === "card"
                      ? "border-b border-gray-200 overflow-scroll h-screen rounded-xl"
                      : "border-b border-gray-200 rounded-xl"
                  }
                >
                  {viewMode === "card" ? (
                    <EmailsCardsUI
                      isMatter={isMatter}
                      emails={isMatter ? matterEmails.nodes : hrEmails.nodes}
                      onSelectEmail={handleSelectEmail}
                      currentSearchInput={currentSearchInput}
                      scrollCardRef={scrollCardRef}
                      scrollCardId={selectedEmailId}
                    />
                  ) : (
                    <EmailsTableUI
                      isMatter={isMatter}
                      emails={isMatter ? matterEmails.nodes : hrEmails.nodes}
                      onSelectEmail={onSelectEmail}
                      onSelectContextMenu={onSelectContextMenu}
                      onToggleFavoriteEmail={onToggleFavoriteEmail}
                      freeWords={currentSearchInput.freeWords || []}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>

          {((isMatter && matterEmails.totalCount > SEARCH_LIMIT) ||
            (!isMatter && hrEmails.totalCount > SEARCH_LIMIT)) && (
            <div className="flex justify-center space-x-5">
              <ButtonUI className="w-40 mb-20" onClick={onShowMore}>
                もっと見る
              </ButtonUI>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
export default SearchEmailsPage;
